import React, { useMemo } from 'react';
import { Img } from 'react-image';
import { LayoutOrientations, CustomEventTypes, TrackerTypes } from '../../globals';
import Panel from '../layout/Panel';
import Spacer from '../layout/Spacer';
import AssetUtility from '../../classes/AssetUtility';
import { Spinner, UINextIcon, UIPreviousIcon } from '../Iconset';
import ARTracker from '../../classes/ARTracker';
import AssetsManager from '../../classes/AssetsManager';
import useEventListener from '../reactUtils/useEventListener';
import useForceUpdate from '../reactUtils/useForceUpdate';

export default function TrackerSwitcherComponent(props: {
	trackerList: ARTracker[],
	currentTracker?: ARTracker,
	onTrackerSwitch: (tracker: any) => void
})
{
	useEventListener(CustomEventTypes.objectNameChanged, () => forceUpdate(), document);
	const forceUpdate = useForceUpdate();

	const trackerObjectName = useMemo(() =>
	{
		// asset takes precedence over object, because when setting an asset it also creates an object, but not vice-versa
		const modelAsset = AssetsManager.getAssetInfo(props.currentTracker?.getModelAssetId() || "");
		if(modelAsset)
		{
			return modelAsset.name;
		}

		// if no asset is assigned, we check for the model name
		const object = props.currentTracker?.modelTracker;
		if(object?.name)
		{
			return object.name.length > 14 ? object.name.substring(0, 14) + '...' : object.name;
		}

		// default to the tracker name, or, if even that is not set, a generic name
		return props.currentTracker?.name || "Object Tracker";
	}, [props.currentTracker]);

	if(!props.trackerList.length || !props.currentTracker || props.currentTracker.trackerType === TrackerTypes.SLAM) return;

	const trackerTypeToName = {
		[TrackerTypes.SLAM]	: "Surface",
		[TrackerTypes.MODEL]: "Target Object",
		[TrackerTypes.IMAGE]: "Target Image",
		[TrackerTypes.QR]	: "Target QR",
	}

	const currentTrackerType = props.currentTracker.trackerType;
	const trackerTypeToTrackerList = {
		[TrackerTypes.SLAM]	: new Array<ARTracker>(),
		[TrackerTypes.MODEL]: new Array<ARTracker>(),
		[TrackerTypes.IMAGE]: new Array<ARTracker>(),
		[TrackerTypes.QR]	: new Array<ARTracker>(),
	}

	//build the tracker lists for each type
	for(const tracker of props.trackerList)
	{
		trackerTypeToTrackerList[tracker.trackerType]?.push(tracker);
	}

	const currentTrackerIndex = trackerTypeToTrackerList[currentTrackerType].findIndex(tracker => tracker.userData.guid === props.currentTracker?.userData.guid);

	return (
		<Panel className={'centerContent'}>
			<span className={"smallText padded extra"}> {trackerTypeToName[currentTrackerType]} </span>
			{
				currentTrackerType === TrackerTypes.MODEL && <b className={"padded extra"}>{trackerObjectName}</b>
			}
			{
				currentTrackerType === TrackerTypes.IMAGE &&
					<div className={'targetImagePreviewSmall centerContent'}>
						<Img
							loader={<Spinner />}
							src={props.currentTracker.imageAssetId ? AssetUtility.getPreviewAssetURL(props.currentTracker.imageAssetId) : '../defaultImageTracker.jpg'}
						/>
					</div>
			}
			{
				trackerTypeToTrackerList[currentTrackerType].length > 1 &&
					<Panel className={'centerContent trackerScrollerContainer'} orientation={LayoutOrientations.Horizontal} expand={0}>
						<UIPreviousIcon data-name={"tracker-switcher-previous"}
							scale={1.25}
							onClick={() => 
							{
								if(currentTrackerIndex > 0)
								{
									props.onTrackerSwitch?.(trackerTypeToTrackerList[currentTrackerType][currentTrackerIndex - 1]);
								}
							}} 
						/>
						<Spacer size={{ x: '8px' }}/>
						{currentTrackerIndex + 1}/{trackerTypeToTrackerList[currentTrackerType].length}
						<Spacer size={{ x: '8px' }}/>
						<UINextIcon data-name={"tracker-switcher-next"}
							scale={1.25}
							onClick={() => 
							{
								if(currentTrackerIndex < trackerTypeToTrackerList[currentTrackerType].length - 1)
								{
									props.onTrackerSwitch?.(trackerTypeToTrackerList[currentTrackerType][currentTrackerIndex + 1]);
								}
							}}
						/>
					</Panel>
			}
		</Panel>
	);
}